// 2. App.tsx
import React, { useRef, useState } from 'react';
import { Box, Button, Input, VStack, useToast, Heading, Text, HStack } from '@chakra-ui/react';
import { GoogleMap, Marker, useLoadScript } from '@react-google-maps/api';
import axios from 'axios';
import AddressAutocomplete from '../deliveryModule/components/AddressAutocomplete/AddressAutocomplete';
import MapWithDirections from '../deliveryModule/components/Directions/MapWithDirections';

const center = { lat: -13.9626, lng: 33.7741 }; // Malawi default

export default function Locations() {
    const [search, setSearch] = useState('');
    const [results, setResults] = useState<any[]>([]);
    const [marker, setMarker] = useState<{ lat: number; lng: number; } | null>(null);
    const toast = useToast();
    const mapRef = useRef<google.maps.Map | null>(null);
    const API_KEY = process.env.REACT_APP_GOOGLE_MAPS_API_KEY!;

    const { isLoaded } = useLoadScript({
        googleMapsApiKey: API_KEY,
        libraries: ['places']
    });

    const handleSearch = async () => {
        try {
            const gRes = await axios.get(`https://maps.googleapis.com/maps/api/place/textsearch/json`, {
                params: {
                    query: search,
                    key: API_KEY
                }
            });

            if (gRes.data.results?.length) {
                const place = gRes.data.results[0];
                setResults([{ name: place.name, lat: place.geometry.location.lat, lng: place.geometry.location.lng }]);
                setMarker(null);
                return;
            }

            const localRes = await axios.get(`http://localhost:5000/api/locations?query=${search}`);
            if (localRes.data.length) {
                setResults(localRes.data);
                setMarker(null);
                return;
            }

            toast({ title: 'Location not found', description: 'Drop a pin to add it.', status: 'info' });
            setResults([]);
        } catch (err) {
            toast({ title: 'Error searching', status: 'error' });
        }
    };

    const handleMapClick = (e: google.maps.MapMouseEvent) => {
        if (e.latLng) {
            setMarker({ lat: e.latLng.lat(), lng: e.latLng.lng() });
        }
    };

    const handleSave = async () => {
        const name = prompt('Give this location a name:');
        if (!name || !marker) return;

        try {
            const res = await axios.post('http://localhost:5000/api/locations', {
                name,
                lat: marker.lat,
                lng: marker.lng
            });
            toast({ title: 'Saved', description: res.data.name, status: 'success' });
            setMarker(null);
        } catch (err) {
            toast({ title: 'Failed to save', status: 'error' });
        }
    };

    return (
        <Box p={5}>
            <Heading mb={4}>Malawi Location Tracker</Heading>
            <VStack spacing={3} align="stretch">
                <HStack>
                    <Input placeholder="Search..." value={search} onChange={(e) => setSearch(e.target.value)} />
                    <Button onClick={handleSearch} colorScheme="blue">Search</Button>
                </HStack>

                {results.map((r, i) => (
                    <Box key={i} p={2} borderWidth={1} borderRadius="md">
                        <Text><strong>{r.name}</strong>: {r.lat}, {r.lng}</Text>
                    </Box>
                ))}

                {isLoaded && (
                    <Box h="400px" w="100%">
                        <GoogleMap
                            mapContainerStyle={{ width: '100%', height: '100%' }}
                            center={center}
                            zoom={13}
                            onLoad={(map) => {
                                mapRef.current = map;
                            }}
                            onClick={handleMapClick}
                        >
                            {marker && <Marker position={marker} />}
                        </GoogleMap>
                    </Box>
                )}

                {marker && <Button colorScheme="green" onClick={handleSave}>Save Dropped Pin</Button>}
            </VStack>
        </Box>
    );
}
